'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/components/ui/hover-card';
import {
  ACTION_KEY_MAP,
  GROUP_KEY_MAP,
  isColumnPermission
} from './role-permissions-matrix-config';

interface PermissionBadgeListProps {
  permissions: string[];
  maxVisible?: number;
  /** Filter the permissions before grouping. Receives the full permission name. */
  filter?: (permName: string) => boolean;
  /** Rendered when the filtered list is empty. */
  emptyFallback?: React.ReactNode;
}

interface GroupedPerms {
  group: string;
  actions: string[];
}

function getActionSegment(permName: string): string {
  const parts = permName.split('.');
  if (isColumnPermission(permName)) {
    return parts.slice(1).join('.');
  }
  return parts.slice(1).join('.') || permName;
}

export function PermissionBadgeList({
  permissions,
  maxVisible = 3,
  filter,
  emptyFallback
}: PermissionBadgeListProps) {
  const t = useTranslations('roles');

  const filteredPermissions = useMemo(() => {
    if (!filter) return permissions;
    return permissions.filter(filter);
  }, [permissions, filter]);

  const grouped = useMemo<GroupedPerms[]>(() => {
    const map = new Map<string, string[]>();
    for (const perm of filteredPermissions) {
      const group = perm.split('.')[0] ?? 'other';
      const action = getActionSegment(perm);
      const arr = map.get(group) ?? [];
      arr.push(action);
      map.set(group, arr);
    }
    return Array.from(map.entries()).map(([group, actions]) => ({
      group,
      actions
    }));
  }, [filteredPermissions]);

  if (grouped.length === 0) {
    return emptyFallback !== undefined ? <>{emptyFallback}</> : null;
  }

  const visible = grouped.slice(0, maxVisible);
  const remaining = grouped.length - maxVisible;

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className='flex flex-wrap items-center gap-1'>
          {visible.map(({ group }) => {
            const key = GROUP_KEY_MAP[group] ?? 'group_other';
            return (
              <Badge
                key={group}
                variant='secondary'
                className='text-xs font-normal'
              >
                {t(key)}
              </Badge>
            );
          })}
          {remaining > 0 && (
            <Badge
              variant='outline'
              className='text-muted-foreground text-xs font-normal'
            >
              {t('morePermissions', { count: remaining })}
            </Badge>
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        align='start'
        side='bottom'
        sideOffset={6}
        collisionPadding={12}
        className='max-h-[min(70vh,22rem)] w-72 overflow-y-auto p-3'
      >
        <div className='space-y-3'>
          {grouped.map(({ group, actions }) => {
            const groupKey = GROUP_KEY_MAP[group] ?? 'group_other';
            return (
              <div key={group} className='space-y-1.5'>
                <p className='text-muted-foreground text-[11px] font-semibold tracking-wide uppercase'>
                  {t(groupKey)}
                </p>
                <div className='flex flex-wrap gap-1'>
                  {actions.map((action) => {
                    const actionKey = ACTION_KEY_MAP[action];
                    const label = actionKey ? t(actionKey) : action;
                    return (
                      <Badge
                        key={action}
                        variant='outline'
                        className='text-[11px] font-normal'
                      >
                        {label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
